import React from 'react';
// Rimosso useState, ChevronDown, ChevronRight
import type { LayerBase, LayerStyleProperties } from '../../../../store/types';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';

interface StyleEditorSectionProps {
    layer: LayerBase & LayerStyleProperties;
    updateLayerStyle: (layerId: string, propertyName: keyof LayerStyleProperties, value: StyleValue) => void;
    isDisabled: boolean;
}

export const FillExtrusionLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
    if (layer.maplibreLayerType !== 'fill-extrusion') return null;
    const { id: layerId } = layer;
    return (
        <>
            <StylePropertyInput
                label="Visibility"
                propertyName="visibility"
                propertyType="select"
                currentValue={layer.visibility}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                selectOptions={[
                    { value: 'visible', label: 'Visible' },
                    { value: 'none', label: 'None' },
                ]}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
        </>
    );
};

export const FillExtrusionPaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
    if (layer.maplibreLayerType !== 'fill-extrusion') return null;
    const { id: layerId } = layer;
    return (
        <>
            <StylePropertyInput
                label="Color"
                propertyName="fillExtrusionColor"
                propertyType="color"
                currentValue={layer.fillExtrusionColor}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
            <StylePropertyInput
                label="Opacity"
                propertyName="fillExtrusionOpacity"
                propertyType="range"
                currentValue={layer.fillExtrusionOpacity}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                numberConfig={{ min: 0, max: 1, step: 0.01 }}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
                hideNumericValueDisplay={true}
            />
            <StylePropertyInput
                label="Height"
                propertyName="fillExtrusionHeight"
                propertyType="number"
                currentValue={layer.fillExtrusionHeight}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                numberConfig={{ min: 0, step: 1 }}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
            <StylePropertyInput
                label="Base Height"
                propertyName="fillExtrusionBase"
                propertyType="number"
                currentValue={layer.fillExtrusionBase}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                numberConfig={{ min: 0, step: 1 }}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
            <StylePropertyInput
                label="Pattern"
                propertyName="fillExtrusionPattern"
                propertyType="text" // MapLibre expects string | null
                currentValue={layer.fillExtrusionPattern ?? ''}
                onStyleChange={(id, prop, val) => {
                    if (isDisabled) return;
                    updateLayerStyle(id, prop as keyof LayerStyleProperties, val === '' ? null : val);
                }}
                layerId={layerId}
                isExpressionPossible={true}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
            <StylePropertyInput
                label="Translate [x, y]"
                propertyName="fillExtrusionTranslate"
                propertyType="text"
                currentValue={JSON.stringify(layer.fillExtrusionTranslate ?? [0,0])}
                onStyleChange={(id, prop, val) => {
                    if (isDisabled) return;
                    try {
                        const parsed = JSON.parse(val as string);
                        if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => typeof n === 'number')) {
                            updateLayerStyle(id, prop as keyof LayerStyleProperties, parsed);
                        } else { /* handle error */ }
                    } catch { /* handle parse error */ }
                }}
                layerId={layerId}
                isExpressionPossible={true}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
            <StylePropertyInput
                label="Translate Anchor"
                propertyName="fillExtrusionTranslateAnchor"
                propertyType="select"
                currentValue={layer.fillExtrusionTranslateAnchor}
                selectOptions={[
                    { value: 'map', label: 'Map' },
                    { value: 'viewport', label: 'Viewport' },
                ]}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
            <StylePropertyInput
                label="Vertical Gradient"
                propertyName="fillExtrusionVerticalGradient"
                propertyType="boolean"
                currentValue={layer.fillExtrusionVerticalGradient}
                onStyleChange={updateLayerStyle}
                layerId={layerId}
                isExpressionPossible={true}
                availablePropertyKeys={layer.availablePropertyKeys}
                disabled={isDisabled}
            />
        </>
    );
};

const FillExtrusionStyleEditor: React.FC<StyleEditorSectionProps> = () => {
    // Questo componente non renderizza più direttamente l'intera UI.
    return null;
};

export default FillExtrusionStyleEditor;